/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Space,
  Descriptions,
  Typography,
  Divider,
  Tag,
  DatePicker,
  Select,
  Row,
  Col,
} from "antd";
import moment from "moment";
import {
  CURRENCY,
  getGstRateAndAmount,
  PAYMENT_METHOD,
} from "../../constants/constent";
import dayjs from "dayjs";
import { displayMessage, postAPI } from "../../utils/common";
import { ERROR_MSG_TYPE, SUCCESS_MESSAGE } from "../../constants/messages";
import {
  BOOKING_CHECKOUT,
  BOOKING_PAY_VERIFY,
  IMAGE_BASE_URL,
} from "../../constants/api";
const { Title } = Typography;

const calculateDiscount = (baseAmount, type, value) => {
  if (!type || !value || parseFloat(value) <= 0) return 0;
  if (type === "flat") return Math.min(parseFloat(value), baseAmount);
  if (type === "percent") {
    const pct = Math.min(parseFloat(value), 100);
    return parseFloat(((baseAmount * pct) / 100).toFixed(2));
  }
  return 0;
};

const CheckoutModal = ({ visible, onClose, booking }) => {
  const {
    customer_name,
    room,
    user,
    total_with_gst,
    advance_amount,
    checkin_date: defaultCheckinDate,
    checkout_date: defaultCheckoutDate,
    payment_status,
    room_price,
    tax_value,
    booking_source,
    booking_segment,
    total_days,
    page_source,
  } = booking;

  const { orders } = booking;
  const { extra_services } = booking;

  // **Filter orders where o_tnx.status is "Pending"**
  const pendingOrders = orders.filter(
    (order) => order.o_tnx.status === "Pending",
  );

  const successOrders = orders.filter(
    (order) => order.o_tnx.status === "Successful",
  );

  const totalSOrderBill = successOrders
    .reduce((total, order) => total + parseFloat(order.total), 0)
    .toFixed(2);

  // **Sum up the full bill details for pending orders**
  //   const totalOrderSubtotal = pendingOrders
  //     .reduce((total, order) => total + parseFloat(order.subtotal), 0)
  //     .toFixed(2);
  //   const totalOrderDiscount = pendingOrders
  //     .reduce((total, order) => total + parseFloat(order.discount), 0)
  //     .toFixed(2);
  //   const totalOrderTax = pendingOrders
  //     .reduce((total, order) => total + parseFloat(order.tax), 0)
  //     .toFixed(2);
  const totalOrderBill = pendingOrders
    .reduce((total, order) => total + parseFloat(order.total), 0)
    .toFixed(2);

  const totalServicesBill = extra_services
    .reduce(
      (total_amount, service) =>
        total_amount + parseFloat(service.total_amount),
      0,
    )
    .toFixed(2);

  const [paymentMethod, setPaymentMethod] = useState("cash");

  const [totalDays, setTotalDays] = useState(0);

  const [selectedCheckoutDate, setSelectedCheckoutDate] =
    useState(defaultCheckoutDate);

  const [totalAmount, setTotalAmount] = useState(
    parseFloat(total_with_gst).toFixed(2),
  );
  const computedAmount =
    parseFloat(totalAmount) +
    parseFloat(totalOrderBill) +
    parseFloat(totalServicesBill) -
    parseFloat(advance_amount);

  const [billAmount, setBillAmount] = useState(
    (Math.abs(computedAmount) < 0.01 ? 0 : computedAmount).toFixed(2),
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [discountType, setDiscountType] = useState(null);
  const [discountValue, setDiscountValue] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);

  const recalculate = (newCheckoutDate) => {
    const pricePerDay = parseFloat(room_price);
    const checkinDate = moment(defaultCheckinDate).startOf("day");
    const checkoutDate = moment(newCheckoutDate).startOf("day");

    let diffDays = checkoutDate.diff(checkinDate, "days");
    diffDays = diffDays === 0 ? 1 : diffDays;
    setTotalDays(diffDays);

    const totalRoomValue = diffDays * pricePerDay;

    const discAmt = calculateDiscount(
      parseFloat(totalRoomValue),
      discountType,
      discountValue,
    );
    setDiscountAmount(discAmt);

    const totalRoomValueWithoutDiscount = totalRoomValue - discAmt;

    // Get GST rate and amount
    const gstRateAmt = getGstRateAndAmount(
      totalRoomValueWithoutDiscount / diffDays,
      diffDays,
    );
    const gstRate = gstRateAmt.gstRate ?? 18;

    let totalBookingBill = 0;

    if (booking_source === "agoda") {
      // Agoda total already includes GST → extract GST portion
      const baseAmount = totalRoomValue * (100 / (100 + gstRate));
      const gstAmount = totalRoomValue - baseAmount;

      // Agoda total already has GST included — use total as-is
      totalBookingBill = totalRoomValue;
    } else {
      // Non-Agoda → Add GST normally
      totalBookingBill = totalRoomValue + gstRateAmt.gstAmount;
    }

    const totalBookingBillWithoutDiscount = parseFloat(
      totalBookingBill.toFixed(2) + discountAmount.toFixed(2),
    ).toFixed(2);

    setTotalAmount(totalBookingBillWithoutDiscount);

    console.log("recalculate: ", {
      pricePerDay: pricePerDay,
      diffDays: diffDays,
      discAmt: discAmt,
      totalRoomValue: totalRoomValue,
      totalRoomValueWithoutDiscount: totalRoomValueWithoutDiscount,
      gstRateAmt: gstRateAmt,
      gstRate: gstRate,
      totalBookingBillWithoutDiscount: totalBookingBillWithoutDiscount,
      totalBookingBill: totalBookingBill,
      totalOrderBill: totalOrderBill,
      totalServicesBill: totalServicesBill,
      discountAmount: discountAmount,
      advance_amount: advance_amount,
    });

    const calculatedBillAmount =
      parseFloat(totalBookingBill) +
      parseFloat(totalOrderBill) +
      parseFloat(totalServicesBill) -
      parseFloat(discountAmount) -
      parseFloat(advance_amount);

    // Normalize near-zero to exactly 0
    const normalizedBillAmount =
      Math.abs(calculatedBillAmount) < 0.01 ? 0 : calculatedBillAmount;

    setBillAmount(normalizedBillAmount.toFixed(2));
  };

  useEffect(() => {
    recalculate(selectedCheckoutDate);
  }, [discountType, discountValue, totalAmount, selectedCheckoutDate]);

  const handleCheckout = async () => {
    setIsProcessing(true);
    let body = {
      id: booking.id,
      checkout_date: selectedCheckoutDate,
      payment_method: paymentMethod,
      discount_type: discountType || null,
      discount_value: discountValue || 0,
    };

    const successFn = function (data) {
      if (data.status === true) {
        setIsProcessing(false);
        onClose();
        if (data.payment_method === "razorpay") {
          const options = {
            key: data.options.key,
            amount: data.options.amount * 100,
            currency: data.options.currency,
            name: data.options.name,
            description: data.options.description,
            image: IMAGE_BASE_URL + data.options.image,
            order_id: data.options.order_id,
            prefill: {
              name: data.options.prefill.name,
              email: data.options.prefill.email,
              contact: data.options.prefill.contact,
            },
            handler: function (response) {
              let body = {
                ...response,
                booking_id: data.booking_id,
              };
              console.log(body);
              const successFn = function (data) {
                onClose();
                displayMessage(SUCCESS_MESSAGE, data.message);
              };
              const errorFn = function () {
                displayMessage(
                  ERROR_MSG_TYPE,
                  "Unable to do payment verification",
                );
              };
              postAPI(BOOKING_PAY_VERIFY, body, successFn, errorFn);
            },
            theme: {
              color: "#F37254",
            },
          };
          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } else {
          setIsProcessing(false);
          onClose();
          displayMessage(SUCCESS_MESSAGE, data.message);
        }
      } else {
        setIsProcessing(false);
        displayMessage(ERROR_MSG_TYPE, data.message);
      }
    };
    const errorFn = function () {
      setIsProcessing(false);
      displayMessage(ERROR_MSG_TYPE, "Unable to checkout");
    };
    postAPI(BOOKING_CHECKOUT, body, successFn, errorFn);

    console.log("handleCheckout: ", body);
  };

  if (!booking || !visible) return null;

  const paymentStatusTag =
    payment_status === "2" ? (
      <Tag color="green">Payment Done</Tag>
    ) : (
      <Tag color="red">Payment Pending</Tag>
    );

  return (
    <Modal
      title="Checkout Booking"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <Divider />
      <Title level={5}>User Information</Title>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="Full Name">{customer_name}</Descriptions.Item>
        {user.phone && user.phone !== "null" && (
          <Descriptions.Item label="Phone">{user.phone}</Descriptions.Item>
        )}
        {user.email && user.email !== "null" && (
          <Descriptions.Item label="Email">
            {user.email || "-"}
          </Descriptions.Item>
        )}
        {user.address && user.address !== "null" && (
          <Descriptions.Item label="Address">
            {user.address || "-"}
          </Descriptions.Item>
        )}
        {user.country && user.country.name && (
          <Descriptions.Item label="Country">
            {user.country
              ? `${user.country.name} ${String.fromCodePoint(
                  ...user.country.emojiU
                    .split(" ")
                    .map((code) => parseInt(code.replace("U+", "0x"), 16)),
                )} `
              : "-"}
          </Descriptions.Item>
        )}
        {user.state && user.state.name && (
          <Descriptions.Item label="State">
            {user.state ? user.state.name : "-"}
          </Descriptions.Item>
        )}
        {user.city && user.city.name && (
          <Descriptions.Item label="City">
            {user.city ? user.city.name : "-"}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Divider />

      <Title level={5}>Room Bill Information</Title>
      <Descriptions bordered column={2}>
        <Descriptions.Item label="Room No">{room.room_no}</Descriptions.Item>
        <Descriptions.Item label="Room Price">{room_price}</Descriptions.Item>
        <Descriptions.Item label="Total Day's">
          <b>{totalDays}</b>
        </Descriptions.Item>
        <Descriptions.Item label="Total Amount">
          {total_with_gst}
        </Descriptions.Item>
      </Descriptions>
      <Divider />
      {extra_services.map((service) => (
        <>
          <Title level={5}>Extra Service Information</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Service">
              {service.name}
            </Descriptions.Item>
            <Descriptions.Item label="Service Price">
              {service.price}
            </Descriptions.Item>
            <Descriptions.Item label="Quantity">
              <b>{service.quantity}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              {service.total_amount}
            </Descriptions.Item>
          </Descriptions>
          <Divider />
        </>
      ))}
      {orders.length > 0 && (
        <>
          <Title level={5}>Order Bill Information</Title>
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Total Orders's">
              <b>{orders.length}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Order's (Unpaid)">
              <b>{pendingOrders.length}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              <b>{parseInt(totalSOrderBill) + parseInt(totalOrderBill)}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Total Paid Amount">
              <b>{totalSOrderBill}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Rest Amount">
              <b>{totalOrderBill}</b>
            </Descriptions.Item>
          </Descriptions>
          <Divider />
        </>
      )}

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Title level={5}>Checkin Date</Title>
          <DatePicker
            disabled
            defaultValue={moment(defaultCheckinDate).startOf("day")}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={12}>
          <Title level={5}>Checkout Date</Title>
          <DatePicker
            key="checkout"
            value={
              selectedCheckoutDate
                ? dayjs(selectedCheckoutDate)
                : dayjs(defaultCheckoutDate)
            }
            onChange={(date) =>
              setSelectedCheckoutDate(
                date ? date.format("YYYY-MM-DD") : defaultCheckoutDate,
              )
            }
            disabledDate={(current) =>
              current && current < moment(defaultCheckinDate).startOf("day")
            }
            style={{ width: "100%" }}
          />
        </Col>
      </Row>

      {page_source !== "qr_scanner" &&
        booking_segment?.toLowerCase() !== "ota" && (
          <>
            <Divider />
            <Title level={5}>{page_source} (Optional)</Title>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <b>Discount Type:</b>
                <Select
                  allowClear
                  placeholder="Select discount type"
                  value={discountType}
                  onChange={(value) => {
                    setDiscountType(value || null);
                    setDiscountValue(0);
                  }}
                  style={{ width: "100%", marginTop: 4 }}
                  options={[
                    { label: "Flat (₹)", value: "flat" },
                    { label: "Percent (%)", value: "percent" },
                  ]}
                />
              </Col>

              <Col span={12}>
                <b>Discount Value:</b>
                <input
                  type="number"
                  min={0}
                  disabled={!discountType}
                  value={discountValue}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value || 0);

                    // GST calculate
                    const gstRateAmt = getGstRateAndAmount(
                      room_price,
                      totalDays || 1,
                    );

                    // Room base (without GST)
                    const roomBase =
                      booking_source === "agoda"
                        ? parseFloat(totalAmount)
                        : parseFloat(totalAmount) - gstRateAmt.gstAmount;

                    if (discountType === "percent") {
                      if (value <= 100) {
                        setDiscountValue(value);
                      } else {
                        displayMessage(
                          ERROR_MSG_TYPE,
                          "Discount percentage cannot exceed 100%",
                        );
                      }
                    } else if (discountType === "flat") {
                      if (value <= roomBase) {
                        setDiscountValue(value);
                      } else {
                        displayMessage(
                          ERROR_MSG_TYPE,
                          `Discount cannot exceed room base amount (₹${roomBase.toFixed(2)})`,
                        );
                      }
                    } else {
                      displayMessage(
                        ERROR_MSG_TYPE,
                        "Please select discount type first",
                      );
                    }
                  }}
                  placeholder={
                    discountType === "percent" ? "Enter %" : "Enter ₹"
                  }
                  style={{
                    width: "100%",
                    marginTop: 4,
                    padding: "4px 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                  }}
                />
              </Col>
            </Row>
          </>
        )}
      <Divider />
      <Space direction="vertical" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Title level={5}>Payment Details</Title>
            <b>Room Amount: </b> {CURRENCY} {totalAmount}
            <br />
            <b>Order Amount: </b> {CURRENCY} {totalOrderBill}
            <br />
            <b>Service Amount: </b> {CURRENCY} {totalServicesBill}
            <br />
            <b>Advance Amount: </b> {CURRENCY} {advance_amount}
            <br />
            <b>Discount: </b> {CURRENCY}{" "}
            {discountAmount.toFixed
              ? discountAmount.toFixed(2)
              : discountAmount}
            <br />
            <b>Remaining Amount: </b> {CURRENCY} {billAmount}
          </Col>
          <Col span={12}>
            <b>Select Payment Method: </b>
            <Select
              value={paymentMethod}
              onChange={(value) => setPaymentMethod(value)}
              style={{ width: "100%" }}
              options={PAYMENT_METHOD.map((method) => ({
                ...method,
                disabled:
                  method.value === "razorpay" && parseFloat(billAmount) <= 0,
              }))}
            />
          </Col>
        </Row>
      </Space>

      <Divider />

      <Space style={{ width: "100%" }} direction="vertical">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Title level={5}>
            Total Bill: {CURRENCY} {billAmount}
          </Title>
          <div>{paymentStatusTag}</div>
        </div>

        <Space style={{ float: "right" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            style={{ marginLeft: 10 }}
            loading={isProcessing}
            onClick={handleCheckout}
            danger
          >
            Confirm Checkout
          </Button>
        </Space>
      </Space>
    </Modal>
  );
};

export default CheckoutModal;
